import React, { useState, useEffect } from 'react';
import { useUser } from './UserContext';
import { subscriptionsAPI } from '../services/api';
import { Subscription } from '../types';
import {
  ExclamationTriangleIcon,
  XMarkIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';

const SubscriptionBanner: React.FC = () => {
  const { currentUser, isAdmin } = useUser();
  const [activeSubscription, setActiveSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (currentUser && !isAdmin && currentUser.companyId) {
      loadActiveSubscription();
    } else {
      setLoading(false);
    }
  }, [currentUser, isAdmin]);

  const loadActiveSubscription = async () => {
    try {
      setLoading(true);
      const subscription = await subscriptionsAPI.getActiveByCompany(currentUser!.companyId!);
      console.log(subscription)
      setActiveSubscription(subscription);
    } catch (err) {
      console.error('Error loading active subscription:', err);
      setActiveSubscription(null);
    } finally {
      setLoading(false);
    }
  };

  // Don't show banner for admin users or if dismissed
  if (isAdmin || dismissed || loading) {
    return null;
  }

  // Don't show banner if user has active subscription
  if (activeSubscription) {
    return null;
  }

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-400 p-4 mb-6">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
        </div>
        <div className="ml-3 flex-1">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-red-800">
              Subscription Required
            </h3>
            <button
              onClick={() => setDismissed(true)}
              className="text-red-400 hover:text-red-600"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-2 text-sm text-red-700">
            <p className="mb-2">
              Your company does not have an active subscription. Some features may be limited.
            </p>
            <div className="bg-white rounded-lg p-3 border border-red-200">
              <div className="flex items-center space-x-4 text-xs">
                <div className="flex items-center">
                  <BuildingOfficeIcon className="h-4 w-4 mr-1 text-gray-500" />
                  <span className="text-gray-600">Company: {currentUser?.companyId}</span>
                </div>
                <div className="flex items-center">
                  <CalendarIcon className="h-4 w-4 mr-1 text-gray-500" />
                  <span className="text-gray-600">Status: Inactive</span>
                </div>
              </div>
            </div>
            <p className="mt-2 text-xs text-red-600">
              Please contact your administrator to activate your subscription.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionBanner;
